import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UploadCloud, FileCheck2, X, Coins, Loader2, Cpu, Fingerprint, Zap, ShieldAlert, BatteryCharging, Cog, Truck, Car, AlertTriangle, ShieldCheck, Sparkles, Check, Lock } from "lucide-react";
import { useI18n } from "@/i18n";
import { svcName, svcDesc, catLabel, catDesc, groupLabel } from "@/i18n/catalog";
import { VehicleCombobox } from "@/components/VehicleCombobox";
import { CarteGriseScanner } from "@/components/CarteGriseScanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const GROUP_COLORS = {
  Performance: "#00E5FF", Émissions: "#8B5CF6", Diagnostic: "#FFB020", Sécurité: "#00E5FF",
  Airbag: "#00E5FF", Batterie: "#00E5A0", Hybride: "#00E0A4", Transmission: "#FFB000",
};
const CAT_ICONS = { ecu: Cpu, gearbox: Cog, airbag: ShieldAlert, battery: BatteryCharging };

// Gearbox (TCU/DSG) offers only two solutions.
const GEARBOX_SERVICE_IDS = ["tcu_stage1", "immo_off"];
const GEARBOX_LABELS = { tcu_stage1: "Tuning", immo_off: "Immo OFF" };


export const NewOrder = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const fileInput = useRef(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [file, setFile] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [ackTuned, setAckTuned] = useState(false);
  const tunedWarn = !!(scan?.tuning_state && (scan.tuning_state.verdict === "tuned_known" || scan.tuning_state.verdict === "likely_tuned"));
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vehicleClass, setVehicleClass] = useState("car");
  const isTruck = vehicleClass === "truck_agri";
  const [engineOn, setEngineOn] = useState(true);
  const [secondaryOn, setSecondaryOn] = useState(false);
  const [dup, setDup] = useState(null);
  const [ecuDetected, setEcuDetected] = useState(false);
  const [secondaryFile, setSecondaryFile] = useState(null);
  const [secondarySelected, setSecondarySelected] = useState([]);
  const [form, setForm] = useState({
    ecu_type: "", ecu_type_other: "",
    vehicle_brand: "", vehicle_model: "", generation: "", vehicle_year: "", engine: "",
    ecu: "", ecu_brand: "", ecu_hw: "", gearbox: "", reading_tool: "", reading_mode: "",
    hardware_nr: "", software_nr: "", software_upgrade: "", customer_note: "",
  });
  const [vehTree, setVehTree] = useState([]);
  useEffect(() => { api.get("/catalog/vehicles").then(({ data }) => setVehTree(data.brands || [])).catch(() => {}); }, []);
  const [ecuTree, setEcuTree] = useState([]);
  useEffect(() => { api.get("/ecu-catalog").then(({ data }) => setEcuTree(data.brands || [])).catch(() => {}); }, []);

  const ecuBrandOptions = ecuTree.map((b) => b.brand);
  const curEcuBrand = ecuTree.find((b) => b.brand === form.ecu_brand);
  const ecuTypeOptions = curEcuBrand ? curEcuBrand.types : [];

  const brandOptions = vehTree.map((b) => b.brand);
  const curBrand = vehTree.find((b) => b.brand === form.vehicle_brand);
  const modelOptions = curBrand ? curBrand.models.map((m) => m.name) : [];
  const curModel = curBrand?.models.find((m) => m.name === form.vehicle_model);
  const genOptions = curModel ? curModel.generations.map((g) => g.code) : [];

  const pickBrand = (b) => setForm((f) => ({ ...f, vehicle_brand: b, vehicle_model: "", generation: "" }));
  const pickModel = (m) => setForm((f) => ({ ...f, vehicle_model: m, generation: "" }));
  const pickGeneration = (code) => {
    // No auto-fill of ECU / year — the user fills every field themselves to avoid confusion.
    setForm((f) => ({ ...f, generation: code }));
  };

  const applyCarteGrise = (r) => {
    if (r?.doc_type === "invoice") { applyInvoice(r); return; }
    setForm((f) => ({
      ...f,
      vehicle_brand: r.brand || f.vehicle_brand,
      vehicle_model: r.model || f.vehicle_model,
      vehicle_year: r.year || f.vehicle_year,
      engine: r.engine_label || f.engine,
    }));
  };

  const applyInvoice = async (r) => {
    try {
      const { data } = await api.post("/profile/prefill", {
        company_name: r.company_name, contact_name: r.contact_name,
        address_line: r.address_line, postal_code: r.postal_code,
        city: r.city, country: r.country, vat_number: r.vat_number,
      });
      await refresh();
      toast.success(`Infos client mises à jour (${(data.updated || []).length} champ(s))`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aucune information exploitable détectée");
    }
    if (r.order_ref) {
      setForm((f) => ({ ...f, customer_note: f.customer_note ? `${f.customer_note}\nRéf. commande : ${r.order_ref}` : `Réf. commande : ${r.order_ref}` }));
    }
  };

  useEffect(() => {
    api.get("/catalog", { params: { vehicle_class: vehicleClass } }).then(({ data }) => {
      setServices(data.services);
      setCategories(data.categories || []);
      const allowed = new Set(data.services.map((x) => x.id));
      setSelected((sel) => sel.filter((id) => allowed.has(id)));
    });
  }, [vehicleClass]);

  const total = selected.reduce((sum, id) => {
    const s = services.find((x) => x.id === id);
    return sum + (s?.credits || 0);
  }, 0);
  const secondaryTotal = secondarySelected.reduce((sum, id) => {
    const s = services.find((x) => x.id === id);
    return sum + (s?.credits || 0);
  }, 0);
  const grandTotal = (engineOn ? total : 0) + (secondaryOn ? secondaryTotal : 0);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleSec = (id) => setSecondarySelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const onFile = async (f) => {
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) { toast.error(t("neworder.fileTooLarge")); return; }
    setFile(f);
    setScan(null);
    setAckTuned(false);
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ecu/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScan(data);
      // Auto-fill ECU brand + type from the binary analysis (only if the user hasn't set them).
      if (data.ecu_brand_guess || data.ecu_hw_guess) {
        setForm((cur) => ({
          ...cur,
          ecu_brand: cur.ecu_brand || data.ecu_brand_guess || "",
          ecu_hw: cur.ecu_hw || data.ecu_hw_guess || "",
        }));
        if (data.ecu_hw_guess) { setEcuDetected(true); toast.success(`ECU détecté : ${[data.ecu_brand_guess, data.ecu_hw_guess].filter(Boolean).join(" ")}`); }
      }
      if (data.instant_available) toast.success(t("neworder.instantToast"));
    } catch (e) {
      // scan is best-effort
    } finally {
      setScanning(false);
    }
  };

  const submit = async (force = false) => {
    if (!engineOn && !secondaryOn) return toast.error("Cochez au moins un type de travail (Moteur ou Boîte)");
    if (!form.vehicle_brand || !form.vehicle_model) return toast.error(t("neworder.fillBrandModel"));
    if (engineOn) {
      if (!file) return toast.error(t("neworder.addFile"));
      if (selected.length === 0) return toast.error(t("neworder.selectSolution"));
      if (tunedWarn && !ackTuned) return toast.error("Confirmez que vous souhaitez traiter ce fichier déjà modifié");
    }
    if (secondaryOn) {
      if (!secondaryFile) return toast.error("Ajoutez le fichier Boîte de vitesses");
      if (secondarySelected.length === 0) return toast.error("Sélectionnez au moins une solution pour la boîte");
    }

    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("vehicle_class", vehicleClass);
    if (force) fd.append("force", "true");
    if (engineOn) {
      fd.append("file", file);
      fd.append("services", JSON.stringify(selected));
      if (secondaryOn && secondaryFile) {
        fd.append("secondary_file", secondaryFile);
        fd.append("secondary_services", JSON.stringify(secondarySelected));
      }
    } else {
      // Gearbox-only: the gearbox file becomes the primary submission.
      fd.set("file", secondaryFile);
      fd.set("services", JSON.stringify(secondarySelected));
      fd.set("ecu_type", "ecu_boite");
    }

    try {
      const { data } = await api.post("/orders", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(t("neworder.fileSent"));
      navigate(`/client/orders/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.order_id) {
        setDup(detail);
      } else {
        toast.error((typeof detail === "string" ? detail : null) || t("neworder.sendError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none focus:ring-1 focus:ring-aurora-cyan/40 placeholder:text-zinc-600";

  return (
    <AppShell mode="client">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">{t("neworder.kicker")}</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("neworder.title")}</h1>
      </div>

      {isTruck && (
        <div data-testid="truck-mode-banner" className="truck-banner rounded-md p-5 mb-6 flex items-center gap-4">
          <div className="truck-rollin w-14 h-14 grid place-items-center rounded-sm bg-[#FFB020]/15 border border-[#FFB020]/50 shrink-0">
            <Truck className="w-8 h-8 text-[#FFB020]" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-xl font-bold uppercase tracking-tight text-[#FFB020]">{t("vc.modeOn")}</div>
            <p className="font-mono text-[11px] text-zinc-300 mt-0.5">{t("vc.modeDesc")}</p>
          </div>
          <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-[#FFB020] border border-[#FFB020]/50 bg-[#FFB020]/10 px-2 py-1 rounded-sm shrink-0 hidden sm:block">24T · AGRI</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Job type selector — defines the work before uploading */}
          <div className="glass rounded-md p-5" data-testid="job-type">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Que souhaitez-vous traiter ?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: "engine", on: engineOn, set: () => setEngineOn((v) => !v), Icon: Cpu, label: "Moteur (ECU)", sub: "Solutions moteur", accent: "#00E5FF", tid: "job-engine" },
                { key: "gearbox", on: secondaryOn, set: () => setSecondaryOn((v) => !v), Icon: Cog, label: "Boîte de vitesses", sub: "TCU / DSG", accent: "#8B5CF6", tid: "job-gearbox" },
              ].map(({ key, on, set, Icon, label, sub, accent, tid }) => (
                <button key={key} type="button" data-testid={tid} onClick={set}
                  className={`flex items-center gap-3 p-4 rounded-sm border text-left transition-all ${on ? "shadow-glow" : "hover:border-white/30"}`}
                  style={on ? { borderColor: accent, background: `${accent}14` } : { borderColor: "rgba(255,255,255,0.1)", background: "rgba(0,0,0,0.3)" }}>
                  <span className="w-9 h-9 grid place-items-center rounded-sm border shrink-0" style={{ borderColor: `${accent}66`, background: `${accent}1a` }}><Icon className="w-4 h-4" style={{ color: accent }} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base uppercase tracking-tight">{label}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{sub}</div>
                  </div>
                  <span className="w-5 h-5 rounded-sm border grid place-items-center shrink-0" style={{ borderColor: on ? accent : "rgba(255,255,255,0.25)", background: on ? accent : "transparent" }}>
                    {on && <Check className="w-3.5 h-3.5 text-black" />}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {engineOn && (<>
          {/* Slave user — encrypted file notice */}
          {user?.is_slave && (
            <div data-testid="slave-notice" className="glass rounded-md p-4 border border-amber-400/40 flex items-start gap-3">
              <Lock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Compte Slave · fichier crypté</div>
                <p className="text-xs text-zinc-300 mt-1">
                  Votre fichier est crypté ({(user.slave_tool || "outil").toString()}). Il ne passe pas par la reconnaissance automatique : envoyez-le tel quel, il sera <b>traité manuellement par un technicien</b>, puis re-crypté et livré dans votre compte.
                </p>
              </div>
            </div>
          )}
          {/* Upload zone */}
          <div
            data-testid="upload-zone"
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); onFile(e.dataTransfer.files[0]); }}
            className={`cursor-pointer glass rounded-md p-10 text-center border-2 border-dashed transition-all ${drag ? "border-aurora-cyan bg-aurora-cyan/5" : "border-zinc-700 hover:border-aurora-cyan/50"}`}
          >
            <input ref={fileInput} type="file" className="hidden" data-testid="file-input" onChange={(e) => onFile(e.target.files[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileCheck2 className="w-8 h-8 text-signal-success" />
                <div className="text-left">
                  <div className="font-mono text-sm text-white">{file.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500">{(file.size / 1024).toFixed(1)} Ko</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="ml-3 text-zinc-500 hover:text-signal-error"><X className="w-5 h-5" /></button>
              </div>
            ) : (
              <>
                <UploadCloud className="w-12 h-12 text-aurora-cyan mx-auto mb-4" />
                <div className="font-display text-lg uppercase tracking-tight">{t("neworder.dropHere")}</div>
                <p className="font-mono text-[10px] text-zinc-500 mt-2 tracking-widest uppercase">{t("neworder.fileHint")}</p>
              </>
            )}
          </div>

          {/* ECU scan result */}
          {(scanning || scan) && (
            <div className="glass rounded-md p-5" data-testid="scan-result">
              <div className="flex items-center gap-2 mb-3">
                <Fingerprint className="w-5 h-5 text-aurora-purple" />
                <h3 className="font-display text-base font-semibold uppercase tracking-tight">{t("neworder.scanTitle")}</h3>
                {scanning && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
              </div>
              {scan && (
                <>
                  {scan.tuning_state && (scan.tuning_state.verdict === "tuned_known" || scan.tuning_state.verdict === "likely_tuned") && (
                    <div data-testid="already-tuned-warning" className="mb-3 p-3 rounded-sm bg-signal-error/10 border border-signal-error/40">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="w-4 h-4 text-signal-error" />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-signal-error">Fichier déjà modifié</span>
                      </div>
                      <p className="text-xs text-zinc-300">
                        Ce fichier semble <b className="text-signal-error">déjà reprogrammé</b>{scan.tuning_state.matched_services?.length ? <> (tune détecté : <b className="uppercase">{scan.tuning_state.matched_services.join(", ")}</b>)</> : null}. Envoyez de préférence un fichier <b>d'origine</b>, ou indiquez-le dans une note pour éviter d'empiler un Stage sur un Stage.
                      </p>
                      <label className="mt-2.5 flex items-start gap-2 cursor-pointer select-none" data-testid="ack-tuned-label">
                        <input type="checkbox" data-testid="ack-tuned-checkbox" checked={ackTuned} onChange={(e) => setAckTuned(e.target.checked)} className="mt-0.5 accent-signal-error w-4 h-4" />
                        <span className="text-[11px] text-zinc-300">Je comprends le risque et je confirme vouloir traiter ce fichier <b>déjà modifié</b>.</span>
                      </label>
                    </div>
                  )}
                  {scan.tuning_state && scan.tuning_state.verdict === "stock" && (
                    <div data-testid="stock-badge" className="mb-3 p-2.5 rounded-sm bg-signal-success/10 border border-signal-success/30 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-signal-success" />
                      <span className="text-xs text-signal-success">Fichier d'origine confirmé — prêt pour un Stage.</span>
                    </div>
                  )}
                  {scan.instant_available && (
                    <div data-testid="instant-badge" className="mb-3 p-3 rounded-sm bg-signal-success/10 border border-signal-success/40">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-signal-success" />
                        <span className="font-mono text-[11px] uppercase tracking-widest text-signal-success">{t("neworder.instantAvail")}</span>
                      </div>
                      {(scan.specs?.stage || scan.specs?.power_hp || scan.specs?.torque_nm) && (
                        <div data-testid="solution-specs" className="flex flex-wrap items-center gap-1.5 mb-2">
                          {scan.specs?.stage && <span data-testid="spec-stage" className="px-2 py-0.5 rounded-full bg-aurora-cyan/15 border border-aurora-cyan/40 font-mono text-[10px] uppercase tracking-widest text-aurora-cyan">{scan.specs.stage}</span>}
                          {scan.specs?.power_hp && <span data-testid="spec-power" className="px-2 py-0.5 rounded-full bg-black/40 border border-white/15 font-mono text-[10px] tracking-widest text-white">{scan.specs.power_hp} ch{scan.specs?.gain_hp ? <span className="text-signal-success"> +{scan.specs.gain_hp}</span> : null}</span>}
                          {scan.specs?.torque_nm && <span data-testid="spec-torque" className="px-2 py-0.5 rounded-full bg-black/40 border border-white/15 font-mono text-[10px] tracking-widest text-white">{scan.specs.torque_nm} Nm{scan.specs?.gain_nm ? <span className="text-signal-success"> +{scan.specs.gain_nm}</span> : null}</span>}
                        </div>
                      )}
                      {scan.instant_services?.length > 0 && (
                        <>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-400">{t("neworder.instantHint")}</div>
                            {scan.instant_services.length > 1 && (() => {
                              const missing = scan.instant_services.filter((sid) => !selected.includes(sid));
                              const missingCr = missing.reduce((sum, sid) => sum + (services.find((x) => x.id === sid)?.credits || 0), 0);
                              return (
                                <button
                                  type="button"
                                  data-testid="instant-add-all"
                                  disabled={missing.length === 0}
                                  onClick={() => setSelected((p) => [...new Set([...p, ...scan.instant_services])])}
                                  className="flex items-center gap-1.5 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-wider transition-colors bg-signal-success text-black font-bold hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Zap className="w-3 h-3" />
                                  {missing.length === 0 ? t("neworder.instantAllAdded") : `${t("neworder.instantAddAll")} · ${missingCr} cr.`}
                                </button>
                              );
                            })()}
                          </div>
                          <div className="flex flex-wrap gap-2" data-testid="instant-solutions">
                            {scan.instant_services.map((sid) => {
                              const svc = services.find((x) => x.id === sid);
                              const on = selected.includes(sid);
                              return (
                                <button
                                  key={sid}
                                  type="button"
                                  data-testid={`instant-solution-${sid}`}
                                  onClick={() => toggle(sid)}
                                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[11px] uppercase tracking-wide transition-colors border ${on ? "bg-signal-success/25 border-signal-success text-signal-success" : "bg-black/40 border-signal-success/40 text-zinc-200 hover:border-signal-success"}`}
                                >
                                  <Zap className="w-3 h-3" />
                                  {svc ? svcName(svc, t) : sid}
                                  {svc?.credits ? <span className="opacity-70">· {svc.credits} cr.</span> : null}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{t("neworder.family")}</div><div className="text-sm text-aurora-cyan">{scan.family || "—"}</div></div>
                    <div><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{t("neworder.size")}</div><div className="text-sm font-mono">{scan.size} o</div></div>
                    <div className="col-span-2"><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{t("neworder.identifiers")}</div><div className="text-xs font-mono text-zinc-300 truncate">{scan.identifiers?.length ? scan.identifiers.join(", ") : "—"}</div></div>
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-3 truncate">SHA-256 · {scan.hash}</div>
                </>
              )}
            </div>
          )}
          </>)}

          {/* Companion gearbox file — shown right under the engine block so it's never lost */}
          {secondaryOn && (
          <div className="glass rounded-md p-6 ring-1 ring-aurora-purple/30" data-testid="gearbox-panel">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 grid place-items-center rounded-sm border shrink-0 border-aurora-purple/40 bg-aurora-purple/10">
                <Cog className="w-5 h-5 text-aurora-purple" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Fichier Boîte de vitesses</h3>
                <p className="font-mono text-[10px] text-zinc-500 leading-snug">Même véhicule · envoyé dans la même demande · facturé séparément</p>
              </div>
            </div>

              <div className="mt-5 space-y-5">
                <label
                  data-testid="gearbox-dropzone"
                  className="flex items-center gap-3 p-4 rounded-sm border border-dashed border-aurora-purple/40 bg-aurora-purple/5 cursor-pointer hover:border-aurora-purple transition-colors"
                >
                  <UploadCloud className="w-6 h-6 text-aurora-purple shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{secondaryFile ? secondaryFile.name : "Choisir le fichier Boîte de vitesses"}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{secondaryFile ? `${(secondaryFile.size / 1024).toFixed(0)} Ko` : "Fichier ECU de la boîte (TCU / DSG…)"}</div>
                  </div>
                  <input type="file" className="hidden" data-testid="gearbox-file-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 30 * 1024 * 1024) { toast.error(t("neworder.fileTooLarge")); return; } setSecondaryFile(f || null); }} />
                </label>

                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Solutions pour la boîte</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {services.filter((s) => GEARBOX_SERVICE_IDS.includes(s.id)).map((s) => {
                      const on = secondarySelected.includes(s.id);
                      return (
                        <button key={s.id} type="button" data-testid={`gearbox-service-${s.id}`} onClick={() => toggleSec(s.id)}
                          className={`text-left p-4 rounded-sm border transition-all ${on ? "border-aurora-purple bg-aurora-purple/10" : "border-white/10 hover:border-white/30 bg-black/30"}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: GROUP_COLORS[s.group] || "#8B5CF6" }}>{s.id === "immo_off" ? "Sécurité" : "Transmission"}</span>
                            <span className={`font-mono text-xs font-bold ${on ? "text-aurora-purple" : "text-zinc-500"}`}>{s.price_from ? t("common.from") + " " : ""}{s.credits} cr.</span>
                          </div>
                          <div className="font-display text-base uppercase tracking-tight">{GEARBOX_LABELS[s.id] || svcName(s, t)}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
          </div>
          )}


          {/* Vehicle info */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">{t("neworder.vehicleInfo")}</h3>
            <div className="mb-5">
              <CarteGriseScanner onExtract={applyCarteGrise} />
              <p className="font-mono text-[10px] text-zinc-500 mt-2 text-center leading-snug">L'IA lit la carte grise (véhicule) ou une facture (infos client) — depuis votre téléphone ou cet ordinateur.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className={form.ecu_type === "autre" ? "" : "sm:col-span-2"}>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.ecuType")}</label>
                <select data-testid="field-ecu_type" className={inputCls} value={form.ecu_type} onChange={(e) => setForm({ ...form, ecu_type: e.target.value })}>
                  <option value="" className="bg-ink-base">{t("neworder.selectPh")}</option>
                  <option value="ecu_moteur" className="bg-ink-base">{t("neworder.ecuEngine")}</option>
                  <option value="ecu_adblue" className="bg-ink-base">{t("neworder.ecuAdblue")}</option>
                  <option value="ecu_boite" className="bg-ink-base">{t("neworder.ecuGearbox")}</option>
                  <option value="airbag" className="bg-ink-base">{t("neworder.airbag")}</option>
                  <option value="autre" className="bg-ink-base">{t("neworder.other")}</option>
                </select>
              </div>
              {form.ecu_type === "autre" && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.specifyType")}</label>
                  <input data-testid="field-ecu_type_other" className={inputCls} placeholder={t("neworder.specifyTypePh")} value={form.ecu_type_other} onChange={(e) => setForm({ ...form, ecu_type_other: e.target.value })} />
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("vc.label")}</label>
                <div className="relative">
                  {isTruck ? <Truck className="w-4 h-4 text-[#FFB020] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" /> : <Car className="w-4 h-4 text-aurora-cyan absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />}
                  <select
                    data-testid="field-vehicle_class"
                    className={`${inputCls} pl-9 ${isTruck ? "border-[#FFB020]/60 focus:border-[#FFB020] focus:ring-[#FFB020]/40 text-[#FFB020]" : ""}`}
                    value={vehicleClass}
                    onChange={(e) => { setVehicleClass(e.target.value); if (e.target.value === "truck_agri") toast(t("vc.toast")); }}
                  >
                    <option value="car">{t("vc.car")}</option>
                    <option value="truck_agri">{t("vc.truck")}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.brand")}</label>
                <VehicleCombobox testid="field-vehicle_brand" icon={isTruck ? Truck : Car} value={form.vehicle_brand} onChange={pickBrand} onPick={pickBrand} options={brandOptions} placeholder="BMW, Audi, VW..." />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.model")}</label>
                <VehicleCombobox testid="field-vehicle_model" value={form.vehicle_model} onChange={pickModel} onPick={pickModel} options={modelOptions} placeholder={form.vehicle_brand ? "Série 3, Golf, A4..." : "Choisissez d'abord une marque"} disabled={!form.vehicle_brand} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1.5">{t("neworder.generation")} <span className="text-zinc-600 normal-case tracking-normal">(ex. E46)</span></label>
                <VehicleCombobox testid="field-generation" value={form.generation} onChange={(v) => setForm({ ...form, generation: v })} onPick={pickGeneration} options={genOptions} placeholder={form.vehicle_model ? "E46, F30, B8..." : "Choisissez d'abord un modèle"} disabled={!form.vehicle_model} />
              </div>
              {[
                ["vehicle_year", t("neworder.year"), "2019"],
                ["engine", t("neworder.engine"), "2.0 TDI 184ch"],
                ["gearbox", t("neworder.gearbox"), "DSG / Manuelle"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{label}</label>
                  <input data-testid={`field-${k}`} className={inputCls} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-2">Marque ECU
                  {ecuDetected && <span data-testid="ecu-detected-badge" className="inline-flex items-center gap-1 normal-case tracking-normal text-signal-success bg-signal-success/12 border border-signal-success/30 px-1.5 py-0.5 rounded-sm text-[9px]"><Sparkles className="w-2.5 h-2.5" /> détecté automatiquement</span>}
                </label>
                <VehicleCombobox testid="field-ecu_brand" icon={Cpu} value={form.ecu_brand} options={ecuBrandOptions} placeholder="Bosch, Continental, Delphi..."
                  onChange={(v) => { setEcuDetected(false); setForm((f) => ({ ...f, ecu_brand: v, ecu_hw: "" })); }}
                  onPick={(v) => { setEcuDetected(false); setForm((f) => ({ ...f, ecu_brand: v, ecu_hw: "" })); }} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1.5">Type ECU <span className="text-zinc-600 normal-case tracking-normal">(ex. EDC17C50)</span></label>
                <VehicleCombobox testid="field-ecu_hw" value={form.ecu_hw} options={ecuTypeOptions} placeholder={form.ecu_brand ? "EDC17C50, MED17.5.5..." : "Marque puis type (ou saisie libre)"}
                  onChange={(v) => { setEcuDetected(false); setForm((f) => ({ ...f, ecu_hw: v })); }}
                  onPick={(v) => { setEcuDetected(false); setForm((f) => ({ ...f, ecu_hw: v })); }} />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.readingTool")}</label>
                <select data-testid="field-reading_tool" className={inputCls} value={form.reading_tool} onChange={(e) => setForm({ ...form, reading_tool: e.target.value })}>
                  <option value="" className="bg-ink-base">{t("neworder.selectPh")}</option>
                  <option value="dimsport" className="bg-ink-base">Dimsport</option>
                  <option value="autotuner" className="bg-ink-base">Autotuner</option>
                  <option value="flex" className="bg-ink-base">Flex</option>
                  <option value="kess" className="bg-ink-base">KESS</option>
                  <option value="autre" className="bg-ink-base">{t("neworder.other")}</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.readingMode")}</label>
                <select data-testid="field-reading_mode" className={inputCls} value={form.reading_mode} onChange={(e) => setForm({ ...form, reading_mode: e.target.value })}>
                  <option value="" className="bg-ink-base">{t("neworder.selectPh")}</option>
                  <option value="obd" className="bg-ink-base">OBD</option>
                  <option value="bench" className="bg-ink-base">Bench</option>
                  <option value="boot" className="bg-ink-base">Boot</option>
                </select>
              </div>

              {[
                ["hardware_nr", t("neworder.hwNr"), "ex: 03L906012..."],
                ["software_nr", t("neworder.swNr"), "ex: 1037..."],
                ["software_upgrade", t("neworder.swUpgrade"), t("neworder.swUpgradePh")],
              ].map(([k, label, ph]) => (
                <div key={k} className={k === "software_upgrade" ? "sm:col-span-2" : ""}>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{label}</label>
                  <input data-testid={`field-${k}`} className={inputCls} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}

              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{t("neworder.noteLabel")}</label>
                <textarea data-testid="field-note" rows={2} className={inputCls} placeholder={t("neworder.notePh")} value={form.customer_note} onChange={(e) => setForm({ ...form, customer_note: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Services grouped by category */}
          {engineOn && categories.map((cat) => {
            const items = services.filter((s) => (s.category || "ecu") === cat.id);
            if (!items.length) return null;
            const Icon = CAT_ICONS[cat.id] || Cpu;
            return (
              <div key={cat.id} className="glass rounded-md p-6" data-testid={`category-${cat.id}`}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 grid place-items-center rounded-sm border shrink-0"
                    style={{ borderColor: `${cat.accent}66`, background: `${cat.accent}1a` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: cat.accent }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{catLabel(cat, t)}</h3>
                    <p className="font-mono text-[10px] text-zinc-500 leading-snug">{catDesc(cat, t)}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {items.map((s) => {
                    const on = selected.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        data-testid={`service-${s.id}`}
                        onClick={() => toggle(s.id)}
                        className={`text-left p-4 rounded-sm border transition-all ${on ? "border-aurora-cyan bg-aurora-cyan/10 shadow-glow" : "border-white/10 hover:border-white/30 bg-black/30"}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: GROUP_COLORS[s.group] }}>{groupLabel(s.group, t)}</span>
                          <span className={`font-mono text-xs font-bold ${on ? "text-aurora-cyan" : "text-zinc-500"}`}>{s.price_from ? t("common.from") + " " : ""}{s.credits} cr.</span>
                        </div>
                        <div className="font-display text-base uppercase tracking-tight">{svcName(s, t)}</div>
                        <p className="text-xs text-zinc-500 mt-1 leading-snug">{svcDesc(s, t)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="glass rounded-md p-6 sticky top-8">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">{t("neworder.summary")}</h3>
            <div className="space-y-2 mb-4 min-h-[60px]">
              {!engineOn ? (
                <p className="font-mono text-xs text-zinc-600">Moteur non sélectionné</p>
              ) : selected.length === 0 ? (
                <p className="font-mono text-xs text-zinc-600">{t("neworder.noSolution")}</p>
              ) : (
                selected.map((id) => {
                  const s = services.find((x) => x.id === id);
                  return (
                    <div key={id} className="flex justify-between text-sm">
                      <span className="text-zinc-300">{s ? svcName(s, t) : id}</span>
                      <span className="font-mono text-zinc-400">{s?.credits}</span>
                    </div>
                  );
                })
              )}
            </div>
            {secondaryOn && secondarySelected.length > 0 && (
              <div className="border-t border-white/10 pt-3 mb-2 space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple flex items-center gap-1.5"><Cog className="w-3.5 h-3.5" /> Boîte de vitesses</div>
                {secondarySelected.map((id) => {
                  const s = services.find((x) => x.id === id);
                  return (
                    <div key={id} className="flex justify-between text-sm">
                      <span className="text-zinc-300">{s ? svcName(s, t) : id}</span>
                      <span className="font-mono text-zinc-400">{s?.credits}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {engineOn && secondaryOn && (
              <div className="border-t border-white/10 pt-3 mb-1 space-y-1.5" data-testid="cost-breakdown">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400 flex items-center gap-1.5"><Cpu className="w-3.5 h-3.5 text-aurora-cyan" /> Sous-total moteur</span>
                  <span className="font-mono text-aurora-cyan" data-testid="engine-subtotal">{total} cr.</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400 flex items-center gap-1.5"><Cog className="w-3.5 h-3.5 text-aurora-purple" /> Sous-total boîte</span>
                  <span className="font-mono text-aurora-purple" data-testid="gearbox-subtotal">{secondaryTotal} cr.</span>
                </div>
              </div>
            )}
            <div className="border-t border-white/10 pt-4 flex items-center justify-between mb-2">
              <span className="font-display uppercase tracking-wide">{t("neworder.total")}</span>
              <span className={`font-mono text-2xl font-bold flex items-center gap-1 ${isTruck ? "text-[#FFB020]" : "text-aurora-cyan"}`}><Coins className="w-5 h-5" />{grandTotal}</span>
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mb-5">{t("neworder.balanceAfter")} : {(user?.credits ?? 0) - grandTotal} {t("neworder.credits")}</div>
            {selected.length > 0 && selected.every((s) => ["fap_off","egr_off","adblue_off","dtc_off","o2_off"].includes(s)) && (
              <div className="mb-4 p-3 rounded-sm border border-signal-success/30 bg-signal-success/5 text-[11px] text-zinc-300 leading-snug">
                <span className="font-mono uppercase tracking-widest text-signal-success text-[10px]">{t("neworder.instantPotential")}</span><br />
                {t("neworder.instantPotentialDesc")}
              </div>
            )}
            <button
              data-testid="submit-order-btn"
              onClick={() => submit()}
              disabled={submitting || (tunedWarn && !ackTuned)}
              className={`w-full flex items-center justify-center gap-2 py-3.5 font-display font-bold uppercase tracking-wider rounded-sm transition-colors shadow-glow disabled:opacity-60 ${isTruck ? "bg-[#FFB020] text-black hover:bg-amber-400" : "bg-aurora-cyan text-black hover:bg-cyan-300"}`}
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("neworder.analyzing")}</> : <>{isTruck ? <Truck className="w-4 h-4" /> : <Cpu className="w-4 h-4" />} {t("neworder.launch")}</>}
            </button>
            {grandTotal > (user?.credits ?? 0) && (
              <p className="mt-3 text-center font-mono text-[10px] text-aurora-cyan uppercase tracking-widest leading-relaxed">
                {t("neworder.completeCredits")}
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!dup} onOpenChange={(v) => (v ? null : setDup(null))}>
        <DialogContent className="max-w-md popover-solid border-white/10" data-testid="duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FFB020]" /> Demande déjà envoyée
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-zinc-500">
              Un fichier identique avec les mêmes solutions a déjà été envoyé récemment{dup?.vehicle ? ` (${dup.vehicle})` : ""}. Souhaitez-vous reprendre cette demande ou en créer une nouvelle quand même ?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <button
              data-testid="dup-resume-btn"
              onClick={() => { const id = dup.order_id; setDup(null); navigate(`/client/orders/${id}`); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-sm bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs hover:bg-cyan-300 transition-colors"
            >
              Reprendre la demande précédente
            </button>
            <button
              data-testid="dup-force-btn"
              onClick={() => { setDup(null); submit(true); }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm border border-white/15 text-zinc-300 font-display font-bold uppercase tracking-wider text-xs hover:border-white/30 transition-colors"
            >
              Envoyer quand même
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};
